import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import Datepicker from "components/Input/Datepicker/Datepicker";
import Selection from "components/Input/Selection/Selection";
import { Stack, Typography } from "@mui/material";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { CustomComponentCommonProps, rules } from "../../utils";
import AmexCountryOptions from "utils/AmexOptions/AmexCountryOptions";
import AmexRegionOptions from "utils/AmexOptions/AmexRegionOptions";
import CheckboxInput from "components/Input/CheckboxInput/CheckboxInput";
import moment from "moment";
import { useTranslation } from "react-i18next";

const AuthorizedSignerSection = () => {
  const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";
  const { t } = useTranslation();
  const pendingRegionRef = useRef(null);
  const [regionOptions, setRegionOptions] = useState([]);
  const [isSameAs, setIsSameAs] = useState(false);
  const { control, setValue } = useFormContext();
  const signerCountry = useWatch({ control, name: "signerCountry" });

  useEffect(() => {
    const countryName = signerCountry?.split(":")?.[1]?.toUpperCase() ?? "";
    const options = AmexRegionOptions(countryName);
    setRegionOptions(options);
  }, [signerCountry]);

  useEffect(() => {
    if (pendingRegionRef.current !== null) {
      const regionExists = regionOptions.some(
        (option) => option.value === pendingRegionRef.current,
      );
      if (regionExists) {
        setValue("signerRegion", pendingRegionRef.current);
        pendingRegionRef.current = null;
      }
    }
  }, [regionOptions]);

  const owners = useWatch({
    control,
    name: "owners",
  });
  const optionsOptions = useMemo(() => {
    return (
      owners?.map((owner, index) => ({
        value: index,
        title: `Owner ${index + 1}`,
      })) || []
    );
  }, [owners]);

  return (
    <Stack gap={2} sx={{ padding: "1rem" }}>
      <Typography variant="h3">
        {t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.subtitle.authorizedSigner`)}
      </Typography>
      <Stack direction="row" gap={2} alignItems="center">
        <CheckboxInput
          label={"Sames as"}
          oncheckFn={(e) => {
            setIsSameAs(e.target.checked);
          }}
        />
        <Selection
          disabled={!isSameAs}
          divStyle={{ flexGrow: 1, flexBasis: "300px" }}
          options={optionsOptions}
          onchangeFn={(e) => {
            const index = e.target.value;
            const selectedOwner = owners?.[index];
            console.log("selectedOwner", selectedOwner);
            if (selectedOwner) {
              setValue("signerLastName", selectedOwner.lastName);
              setValue("signerFirstName", selectedOwner.firstName);
              setValue("signerIdNo", selectedOwner.id_no);
              setValue("signerDateBirth", moment(selectedOwner.dateBirth));
              setValue("signerAddress", selectedOwner.address);
              setValue("signerCountry", selectedOwner.country);
              pendingRegionRef.current = selectedOwner.region;
            }
          }}
        />
      </Stack>

      <Stack sx={{ margin: "1rem 0" }}>
        <Stack gap={3}>
          <Stack direction="row" gap={2} flexWrap={"wrap"}>
            <Controller
              name={`signerLastName`}
              control={control}
              defaultValue=""
              rules={rules({
                maxLength: 20,
                required: true,
                validationTags: [
                  "noLeadingWhitespace",
                  "noFullwidthCharacters",
                  "noSpecialSpaces",
                ],
              })}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <OutlinedText
                  {...CustomComponentCommonProps(
                    t(
                      `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.lastName`,
                    ),
                    value,
                    "OutlinedText",
                    true,
                    true,
                  )}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1 }}
                  sxParams={{
                    textError: error,
                  }}
                  errorMessage={error?.message || ""}
                />
              )}
            />
            <Controller
              name={`signerFirstName`}
              control={control}
              defaultValue=""
              rules={rules({
                maxLength: 20,
                required: true,
                validationTags: [
                  "noLeadingWhitespace",
                  "noFullwidthCharacters",
                  "noSpecialSpaces",
                ],
              })}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <OutlinedText
                  {...CustomComponentCommonProps(
                    t(
                      `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.firstName`,
                    ),
                    value,
                    "OutlinedText",
                    true,
                    true,
                  )}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1 }}
                  sxParams={{
                    textError: error,
                  }}
                  errorMessage={error?.message || ""}
                />
              )}
            />
          </Stack>

          <Controller
            name={`signerIdNo`}
            control={control}
            defaultValue=""
            render={({ field: { value, onChange } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.idNo`,
                  ),
                  value,
                  "OutlinedText",
                  false,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
              />
            )}
          />

          <Controller
            name={`signerDateBirth`}
            control={control}
            rules={rules({
              required: true,
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Datepicker
                mandatory
                divStyle={{ flexGrow: 1 }}
                onchangeFn={onChange}
                value={value}
                label={t(
                  `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.dateOfBirth`,
                )}
                sxParams={{
                  datepickerError: error,
                }}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            name={`signerAddress`}
            control={control}
            defaultValue=""
            rules={rules({
              maxLength: 200,
              required: true,
              validationTags: [
                "noLeadingWhitespace",
                "noFullwidthCharacters",
                "noSpecialSpaces",
              ],
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.address`,
                  ),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                sxParams={{
                  textError: error,
                }}
                errorMessage={error?.message}
              />
            )}
          />

          {/* Country */}
          <Controller
            name={`signerCountry`}
            control={control}
            defaultValue=""
            rules={rules({ required: true })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.country`,
                  ),
                  value || "",
                  "Selection",
                  true,
                  true,
                )}
                options={AmexCountryOptions("all")}
                onchangeFn={onChange}
                errorMessage={error?.message || ""}
                sxParams={{
                  selectionError: error,
                }}
              />
            )}
          />

          <Controller
            name={`signerRegion`}
            control={control}
            defaultValue=""
            rules={rules({ required: true })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.region`,
                  ),
                  value || "",
                  "Selection",
                  true,
                  true,
                )}
                options={regionOptions}
                onchangeFn={onChange}
                errorMessage={error?.message || ""}
                sxParams={{
                  selectionError: error,
                }}
              />
            )}
          />

          <Controller
            name={`signerPosition`}
            control={control}
            defaultValue=""
            rules={rules({
              maxLength: 20,
              required: true,
              validationTags: [
                "noLeadingWhitespace",
                "noFullwidthCharacters",
                "noSpecialSpaces",
              ],
            })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.authorizedSigner.title`,
                  ),
                  value,
                  "OutlinedText",
                  true,
                  true,
                )}
                onchangeFn={onChange}
                divStyle={{ flexGrow: 1 }}
                mandatory
                sxParams={{
                  textError: error,
                }}
                errorMessage={error?.message}
              />
            )}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default React.memo(AuthorizedSignerSection);
